<template>
  <v-card
    id="booking-card"
    class="max-shadow"
  >
    <v-toolbar class="white">
      <v-toolbar-title class="py-2">
        <div class="title-2">
          Bookings
        </div>
      </v-toolbar-title>
      <v-spacer />

      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-btn
            depressed
            v-on="on"
          >
            {{ filterBy }}
            <v-icon class="pl-2">
              arrow_drop_down
            </v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-tile
            v-for="(date, index) in dateRange"
            :key="index"
            @click="updateDate(date)"
          >
            <v-list-tile-title>{{ date }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </v-menu>
    </v-toolbar>

    <v-card px-1>
      <v-layout
        row
        class="pt-2 pb-1"
        xl12
      >
        <v-card-title
          primary-title
          class="display-1"
        >
          Meeting Rooms Log
        </v-card-title>
        <v-spacer />
        <v-layout
          row
          wrap
          class="pt-2 pb-1"
          xl12
        >
          <v-flex
            xs12
            sm6
          >
            <count-widget
              color="pink darken-1"
              icon="mdi-ticket-outline"
              title="Total Meeting Rooms"
              :value="totalFacilities"
              style="max-width: 300px; margin: 0 auto"
            />
          </v-flex>
          <v-flex
            xs12
            sm6
          >
            <count-widget
              color="orange darken-2"
              icon="mdi-ticket-outline"
              title="Total Bookings"
              :value="totalBookings"
              style="max-width: 300px; margin: 0 auto"
            />
          </v-flex>
        </v-layout>
      </v-layout>
      <div class="px-3">
        <v-progress-linear
          :active="facilityLoading"
          :indeterminate="true"
          height="2"
          top
        />
      </div>
      <div>
        <v-card-text>
          <barChart
            :chart-data="facilityLog"
            :options="chartOptions"
            :height="chartHeight"
          />
        </v-card-text>
      </div>

      <v-card-title
        primary-title
        class="display-1"
      >
        User Booking Log
      </v-card-title>

      <div class="px-3">
        <v-progress-linear
          :active="userLoading"
          :indeterminate="true"
          height="2"
          top
        />
      </div>

      <div>
        <v-card-text>
          <barChart
            :chart-data="usersLog"
            :options="chartOptions"
            :height="chartHeight"
          />
        </v-card-text>
      </div>
    </v-card>

    <v-snackbar
      v-model="snackbar"
      :color="snackColor"
      :timeout="0"
      bottom
    >
      {{ snackbarMessage }}
      <v-btn
        flat
        @click.native="snackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import * as moment from 'moment';
import { functions } from '@/firebase';
import TicketCountwidget from '@/components/common/TicketCountWidget';
import _get from 'lodash/get';
import barChart from '@/components/common/BarChart';

export default {
  name: 'FacilityAnalytics',
  components: {
    'count-widget': TicketCountwidget,
    barChart
  },
  data() {
    return {
      facilityLoading: true,
      userLoading: true,
      startAt: '',
      endAt: '',
      snackbar: false,
      snackColor: 'primary',
      snackbarMessage: '',
      dateRange: [
        'Today',
        'Yesterday',
        'This week',
        'This month',
        'Last 30 days',
        'Last 60 days',
        'Last 90 days',
        'This Year',
        'Last calendar year',
      ],
      log: {},
      totalBookings: 0,
      totalFacilities: 0,
      filterBy: 'Today',
      usersLog: [],
      facilityLog: [],
      chartHeight: 150,
      chartOptions: {
        responsive: true,
        legend: false,
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true
              },
              gridLines: {
                display: false
              }
            }
          ],
          xAxes: [
            {
              gridLines: {
                display: false
              }
            }
          ]
        }
      },
      yStepSize: 0.25
    };
  },
  computed: {
    role() {
      return _get(this.$store.state.profile, 'role');
    },
    cid() {
      return _get(this.$store.state.profile, 'cid');
    }
  },
  mounted() {
    this.startAt = moment()
      .startOf('day')
      .valueOf();
    this.endAt = moment()
      .endOf('day')
      .valueOf();
    this.fetchLog();
  },
  methods: {
    fetchTotalTickets() {
      const totalTickets = functions.httpsCallable(
        'bigQuery-helpDesk-totalTickets'
      );
      totalTickets({
        startAt: this.startAt,
        endAt: this.endAt,
        categoryId: this.category.id
      })
        .then()
        .catch(err => {
          console.error(err);
        });
    },
    fetchUserAnalytics() {
      this.userLoading = true;
      const totalTickets = functions.httpsCallable(
        'bigQuery-facility-userBookings'
      );
      totalTickets({
        startAt: this.startAt,
        endAt: this.endAt
      })
        .then(result => {
          const data = result.data || {};

          let users = [];

          for (const log of data) {
            users.push([log.name, log.count]);
          }

          while (users.length < 10) {
            users.push(['', 0]);
          }

          users.sort((a, b) => {
            return b[1] - a[1];
          });

          users = users.slice(0, 20);

          // option object
          this.usersLog = {
            datasets: [
              {
                label: 'Bookings',
                backgroundColor: '#3366cc',
                data: users.map(log => log[1])
              }
            ],
            labels: users.map(log => log[0])
          };

          this.userLoading = false;
        })
        .catch(err => {
          console.error(err);
        });
    },
    fetchLog() {
      this.fetchFacilitiesAnalytics();
      this.fetchUserAnalytics();
    },
    fetchFacilitiesAnalytics() {
      this.facilityLoading = true;
      const bookings = functions.httpsCallable(
        'bigQuery-facility-meetingRoomBookings'
      );
      bookings({
        startAt: this.startAt,
        endAt: this.endAt
      })
        .then(result => {
          const data = result.data || [];
          const facilityBookings = data[0] || [];
          this.totalFacilities = data[1] || 0;

          const facilities = [];
          const bookings = [];
          let count = 0;
          for (const log of facilityBookings) {
            facilities.push(log.name || '(delted)');
            bookings.push(log.count);
            count += log.count;
          }

          this.totalBookings = count;

          while (facilities.length < 10) {
            facilities.push('');
            bookings.push(0);
          }

          // option object
          this.facilityLog = {
            datasets: [
              {
                label: 'Bookings',
                backgroundColor: '#3366cc',
                data: bookings
              }
            ],
            labels: facilities
          };

          this.facilityLoading = false;
        })
        .catch(err => {
          console.error(err);
        });
    },
    updateDate(date) {
      if (this.filterBy === date) return;

      this.filterBy = date;

      switch (this.filterBy) {
        case 'Today':
          this.startAt = moment().startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Yesterday':
          this.startAt = moment().subtract(1, 'days').startOf('day').valueOf();
          this.endAt = moment().subtract(1, 'days').endOf('day').valueOf();

          break;

        case 'This week':
          this.startAt = moment().startOf('week').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'This month':
          this.startAt = moment().startOf('month').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 30 days':
          this.startAt = moment().subtract(29, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 60 days':
          this.startAt = moment().subtract(59, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last 90 days':
          this.startAt = moment().subtract(89, 'days').startOf('day').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'This Year':
          this.startAt = moment().startOf('year').valueOf();
          this.endAt = moment().endOf('day').valueOf();

          break;

        case 'Last calendar year':
          this.startAt = moment().startOf('year').subtract(1, 'year').valueOf();
          this.endAt = moment().endOf('year').subtract(1, 'year').valueOf();
          break;

        default:
          break;
      }

      this.fetchLog();
    }
  }
};
</script>

<style>
</style>